import { defineComponent, PropType, onMounted, computed, ComputedRef, watch } from 'vue';
import Sunburst from 'sunburst-chart';
import BaseReportComponent from '@/report/components/BaseReportComponent';
import CtaChartModel from '../models/CtaChartModel';

// Define proper types for the chart data
export class SunburstNode {
    name: string;
    value?: number;
    label?: string;
    nodeKey?: string;
    color?: string;
    children?: SunburstNode[];
    size?: number;
    nodeClassName?: string = '';
    [key: string]: any; // For any additional properties

    constructor(obj?: Partial<SunburstNode>) {
        if (obj) {
            Object.assign(this, obj);
        }
    }
}

export default defineComponent({
    extends: BaseReportComponent,
    props: {
        chartId: { type: Number, default: 0 },
        domainArray: { type: Object as PropType<Record<string, number>>, required: true },
        domainPreferred: { type: Array as PropType<string[]>, required: true },
        width: {
            type: Number,
            default: 400,
        },
        height: {
            type: Number,
            default: 400,
        },
        forceBigOverlay: { type: Boolean, default: false },
    },
    setup(props) {
        const chartData: ComputedRef<SunburstNode> = computed(() => {
            const entries = Object.entries(props.domainArray);
            const calculatedDomainModels: CtaChartModel[] = [];

            entries.forEach(([domainName, domainValue]) => {
                const name = removeUntilUppercase(domainName);

                const domainModel: CtaChartModel = {
                    name: name,
                    value: domainValue,
                    depth: domainValue / 10 + 2, // + 2 for better visualization
                    color: selectColor(domainName, domainValue),
                    size: domainName.includes('action') ? 25 : 33.33,
                    nodeNameClass: props.domainPreferred.includes(name) ? 'preferred' : '',
                };

                calculatedDomainModels.push(domainModel);
            });
            const firstDomain = getFirstDomain(entries[0][0]);
            return {
                name: 'root',
                size: 0,
                children: createChartData(calculatedDomainModels, firstDomain),
            };
        });

        const domainTranslations: ComputedRef<string[]> = computed(() => {
            return Object.keys(props.domainArray)
                .map((domainName) => {
                    return getDomainString(domainName);
                })
                .filter((value, index, array) => array.indexOf(value) === index)
                .sort((a, b) => a.localeCompare(b));
        });

        const labelValues: ComputedRef<{ key: string; value: string }[]> = computed(() => {
            const entries = Object.entries(props.domainArray);

            return entries
                .map(([key, rawValue]) => ({
                    key: removeUntilUppercase(key),
                    value: formatPercentage(rawValue),
                }))
                .filter((label) => label.value !== '');
        });

        const getLeafSize = (node: SunburstNode): number => {
            if (!node.children || node.children.length === 0) {
                return node.size ?? 0;
            }
            return node.children.reduce((sum, child) => sum + getLeafSize(child), 0);
        };

        const getNodeConstructKey = (node: SunburstNode): string | undefined => {
            if (node.nodeKey) return node.nodeKey;
            if (node.children && node.children.length > 0) {
                return getNodeConstructKey(node.children[0]);
            }
            return undefined;
        };

        const valueLabels = computed(() => {
            const root = chartData.value;
            const children = root.children ?? [];
            const totalSize = children.reduce((sum, child) => sum + getLeafSize(child), 0);
            if (totalSize <= 0) return [];

            const chartRadius = 200;
            const labelRadius = chartRadius * 0.55;
            const centerX = 200;
            const centerY = 200;

            // Compute angular midpoint for each construct from the data model
            const constructAngles = new Map<string, number>();
            let cumSize = 0;
            for (const child of children) {
                const childSize = getLeafSize(child);
                const constructKey = getNodeConstructKey(child);

                if (constructKey) {
                    const startFraction = cumSize / totalSize;
                    const endFraction = (cumSize + childSize) / totalSize;
                    const midAngle = ((startFraction + endFraction) / 2) * 2 * Math.PI;
                    constructAngles.set(constructKey, midAngle);
                }

                cumSize += childSize;
            }

            // Per-construct labels
            return labelValues.value
                .filter((x) => parseFloat(x.value) > 1)
                .map((label) => {
                    const angle = constructAngles.get(label.key) ?? 0;
                    return {
                        key: label.key,
                        value: label.value,
                        left: Math.round(centerX + labelRadius * Math.sin(angle)),
                        top: Math.round(centerY - labelRadius * Math.cos(angle)),
                    };
                });
        });

        const selectColor = (domainName: string, domainValue: number): string => {
            if (domainValue === 1) {
                return 'lightgray';
            } else if (domainName.includes('action')) {
                return `rgba(85, 170, 156, ${domainValue / 50})`;
            } else if (domainName.includes('attention')) {
                return `rgba(236, 172, 114, ${domainValue / 50})`;
            } else if (domainName.includes('operating')) {
                return `rgba(171, 60, 48, ${domainValue / 50})`;
            }
        };

        const getFirstDomain = (domainName: string): number => {
            if (domainName.includes('action')) {
                return 1;
            } else if (domainName.includes('attention')) {
                return 2;
            } else if (domainName.includes('operating')) {
                return 3;
            } else {
                return 1;
            }
        };

        const removeUntilUppercase = (input: string): string => {
            if (!input || typeof input !== 'string') {
                return '';
            }

            for (let i = 0; i < input.length; i++) {
                const char = input.charAt(i);
                if (char === char.toUpperCase() && char !== char.toLowerCase()) {
                    return input.substring(i);
                }
            }

            return input;
        };

        const getDomainString = (str: string): string => {
            for (let i = 0; i < str.length; i++) {
                if (str[i] >= 'A' && str[i] <= 'Z') {
                    return str.substring(0, i);
                }
            }
            return str;
        };

        const createChartData = (data: CtaChartModel[], caseNumber: number = 1): SunburstNode[] => {
            const getGroupConfigurations = () => {
                switch (caseNumber) {
                    case 1:
                        return {
                            firstGroup: ['Power', 'Meaning', 'Affect'],
                            secondGroup: ['Random', 'Closed', 'Open'],
                            thirdGroup: ['Move', 'Follow', 'Oppose', 'Bystand'],
                        };
                    case 2:
                        return {
                            firstGroup: ['Random', 'Closed', 'Open'],
                            secondGroup: ['Move', 'Follow', 'Oppose', 'Bystand'],
                            thirdGroup: ['Power', 'Meaning', 'Affect'],
                        };
                    case 3:
                        return {
                            firstGroup: ['Power', 'Meaning', 'Affect'],
                            secondGroup: ['Move', 'Follow', 'Oppose', 'Bystand'],
                            thirdGroup: ['Random', 'Closed', 'Open'],
                        };
                    default:
                        return {
                            firstGroup: ['Power', 'Meaning', 'Affect'],
                            secondGroup: ['Random', 'Closed', 'Open'],
                            thirdGroup: ['Move', 'Follow', 'Oppose', 'Bystand'],
                        };
                }
            };

            const { firstGroup, secondGroup, thirdGroup } = getGroupConfigurations();

            const getDefaultSize = (groupType: 'first' | 'second' | 'third') => {
                switch (groupType) {
                    case 'first':
                    case 'second':
                        return 33.33;
                    case 'third':
                        return 25;
                }
            };

            const chartData: SunburstNode[] = [
                new SunburstNode({
                    name: '',
                    size: 10,
                    color: 'white',
                }),
                ...firstGroup.map((name) => {
                    return new SunburstNode({
                        name: '',
                        size: 0,
                        color: 'white',
                        children: [
                            createChildren(data.find((item) => item.name === name) ?? new CtaChartModel({ size: getDefaultSize('first') }), true),
                        ],
                    });
                }),
                new SunburstNode({
                    name: '',
                    size: 20,
                    color: 'white',
                }),
                ...secondGroup.map((name) => {
                    return new SunburstNode({
                        name: '',
                        size: 0,
                        color: 'white',
                        children: [
                            createChildren(data.find((item) => item.name === name) ?? new CtaChartModel({ size: getDefaultSize('second') }), true),
                        ],
                    });
                }),
                new SunburstNode({
                    name: '',
                    size: 20,
                    color: 'white',
                }),
                ...thirdGroup.map((name) => {
                    return new SunburstNode({
                        name: '',
                        size: 0,
                        color: 'white',
                        children: [
                            createChildren(data.find((item) => item.name === name) ?? new CtaChartModel({ size: getDefaultSize('third') }), true),
                        ],
                    });
                }),
                new SunburstNode({
                    name: '',
                    size: 10,
                    color: 'white',
                }),
            ];

            return chartData;
        };

        const createChildren = (data?: CtaChartModel, showLabel: boolean = false): SunburstNode => {
            if (data == null || data.depth <= 0) {
                return new SunburstNode({
                    name: data.name,
                    label: data.name,
                    nodeKey: data.name,
                    value: data.value,
                    size: data.size,
                    color: 'white',
                    nodeClassName: data.nodeNameClass,
                });
            } else {
                data.depth--;
                return new SunburstNode({
                    name: showLabel ? formatPercentage(data.value) : '',
                    label: data.name,
                    nodeKey: data.name,
                    value: data.value,
                    size: 0,
                    color: data.color,
                    children: [createChildren(data, false)],
                });
            }
        };

        const formatPercentage = (value?: number): string => {
            if (value == null || Number.isNaN(value)) {
                return '';
            }

            const rounded = Math.round(value * 10) / 10;
            return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
        };

        const renderChart = () => {
            const chartContainer = document.getElementById(`chart-${props.chartId}`) as HTMLElement | null;
            if (!chartContainer) return;
            // Clear previous chart if exists
            chartContainer.innerHTML = '';
            const chart = new Sunburst(chartContainer);
            chart
                .data(chartData.value)
                .width(400)
                .height(400)
                .size('size')
                .color('color')
                .nodeClassName('nodeClassName')
                .strokeColor('strokeColor')
                .excludeRoot(true)
                .labelOrientation('angular')
                .showTooltip(() => false)
                .onClick(() => null)
                .transitionDuration(0)
                .radiusScaleExponent(1);
        };

        onMounted(() => {
            renderChart();
        });

        watch(
            () => props.domainArray,
            () => {
                renderChart();
            },
            { deep: true },
        );

        return {
            domainTranslations,
            valueLabels,
        };
    },
});
